import { assertNever } from "@msys/common";
import {
  ApplyItemActionInput,
  ApplyItemActionSetRecommendationsOperation,
  EnterItemProps2ValueEntry,
} from "../../../../clients/graphqlTypes.js";

/**
 * The single `ApplyItemAction` each question kind is answered with. One
 * function per kind rather than one mapper over the question union: the
 * renderers own the values being submitted, and the question only supplies the
 * item id.
 */

export function productAction(
  itemId: string,
  selection: { supplierId: string; articleNumber: string } | null
): ApplyItemActionInput {
  return {
    setSelectProduct: {
      itemId,
      selection: selection
        ? {
            productSupplierId: selection.supplierId,
            productArticleNumber: selection.articleNumber,
          }
        : null,
    },
  };
}

export function placeholderForTemplateAction(
  itemId: string,
  selectedTemplateId: string | null
): ApplyItemActionInput {
  return {
    setSelectPlaceholderForTemplateType: { itemId, selectedTemplateId },
  };
}

export function decisionAction(
  itemId: string,
  decisionSubitemsPreselection: Array<string>
): ApplyItemActionInput {
  return {
    setDecisionSubitemsPreselection: { itemId, decisionSubitemsPreselection },
  };
}

export function contingencyAction(
  itemId: string,
  decisionContingentItemPreselection: boolean | null
): ApplyItemActionInput {
  return {
    setDecisionContingencyPreselection: {
      itemId,
      decisionContingentItemPreselection,
    },
  };
}

export function recommendationsAction(
  itemId: string,
  recommendations: Array<{
    recommendationId: string;
    operation: ApplyItemActionSetRecommendationsOperation;
  }>
): ApplyItemActionInput {
  return { setRecommendations: { itemId, recommendations } };
}

export type PropertyAnswerEntry =
  | { kind: "Props2Text"; key: string; value: string | null }
  | { kind: "Props2TextArray"; key: string; value: Array<string> | null }
  | { kind: "Props2Number"; key: string; value: number | null }
  | { kind: "Props2NumberArray"; key: string; value: Array<number> | null }
  | { kind: "Props2Bool"; key: string; value: boolean | null };

/**
 * Whether a prop was answered at all. `0` and `false` are answers; an empty
 * text or list is the form's untouched state, and submitting it would
 * overwrite whatever the document holds with nothing.
 */
export function hasValue(
  value: string | number | boolean | Array<unknown> | null | undefined
): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;

  return true;
}

export function propertiesAction(
  itemId: string,
  entries: Array<PropertyAnswerEntry>
): ApplyItemActionInput {
  return {
    setEnterPropsValueEntries: {
      itemId,
      entries: entries.filter(entry => hasValue(entry.value)).map(boxEntry),
    },
  };
}

function boxEntry(entry: PropertyAnswerEntry): EnterItemProps2ValueEntry {
  switch (entry.kind) {
    case "Props2Text": {
      return { text: { key: entry.key, valueText: entry.value } };
    }
    case "Props2TextArray": {
      return { textArray: { key: entry.key, valueTextArray: entry.value } };
    }
    case "Props2Number": {
      return { number: { key: entry.key, valueNumber: entry.value } };
    }
    case "Props2NumberArray": {
      return {
        numberArray: { key: entry.key, valueNumberArray: entry.value },
      };
    }
    case "Props2Bool": {
      return { bool: { key: entry.key, valueBool: entry.value } };
    }
    default: {
      return assertNever(entry);
    }
  }
}
